import { IFormJBPHPContactChangeRequest } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { Dropdown } from 'legacy/lut/library/dropdown'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { useFormField } from 'cng-common/features/form'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Logic, Operator, Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { getJbpHpContactsCrud } from 'partner360/features/hpContacts/lib'

import {
  PRACTICE_BUILDER_CRUD_KEY,
  PRACTICE_BUILDER_PAGE_SIZE,
  STRATEGIC_INITIATIVE_TO_BUSINESS_UNIT_MAPPING,
} from './constants'

interface IPracticeBuilderProps {
  className: string
  initialValue?: string
  formKey: string
  partnerId: string
  roles: string[]
  strategicInitiative?: string
  isDisabled?: boolean
  placeholder?: string
}

type TPracticeBuilderOption = IFormJBPHPContactChangeRequest & {
  value: string
  fullName: string
  displayLabel: string
  legacyValue: string
}

const normalizeBusinessUnit = (value: string) => value.trim().toLowerCase()
const normalizeStrategicInitiative = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, '')

const splitBusinessUnits = (value: string) =>
  value
    .split('/')
    .map((item) => normalizeBusinessUnit(item))
    .filter(Boolean)

const getStrategicInitiativeBusinessUnits = (strategicInitiative: string) => {
  const normalizedStrategicInitiative = normalizeStrategicInitiative(strategicInitiative)

  return (
    Object.entries(STRATEGIC_INITIATIVE_TO_BUSINESS_UNIT_MAPPING).find(([initiative]) => {
      const normalizedInitiative = normalizeStrategicInitiative(initiative)

      return (
        normalizedStrategicInitiative === normalizedInitiative ||
        normalizedStrategicInitiative.includes(normalizedInitiative)
      )
    })?.[1] || []
  )
}

const isBusinessUnitMatchingSI = (
  contactBusinessUnit: string | null | undefined,
  strategicInitiative: string
): boolean => {
  if (!strategicInitiative || !contactBusinessUnit) {
    return false
  }

  const validBUs = getStrategicInitiativeBusinessUnits(strategicInitiative)
  if (validBUs.length === 0) {
    return false
  }

  const normalizedContactBusinessUnit = normalizeBusinessUnit(contactBusinessUnit)
  const contactBUs = splitBusinessUnits(contactBusinessUnit)

  return validBUs.some((validBU) => {
    const normalizedValidBU = normalizeBusinessUnit(validBU)
    const validBUTokens = splitBusinessUnits(validBU)

    return (
      normalizedContactBusinessUnit === normalizedValidBU ||
      validBUTokens.every((token) => contactBUs.includes(token))
    )
  })
}

const getPracticeBuilderFullName = (contact: IFormJBPHPContactChangeRequest) =>
  [contact.FirstName, contact.LastName].filter(Boolean).join(' ').trim() || contact.Email || ''

const getPracticeBuilderDisplayLabel = (contact: IFormJBPHPContactChangeRequest) => {
  const fullName = getPracticeBuilderFullName(contact)

  return contact.Email ? `${fullName} <${contact.Email}>` : fullName
}

export const PracticeBuilder: FC<IPracticeBuilderProps> = ({
  initialValue = '',
  className,
  formKey,
  partnerId,
  roles,
  strategicInitiative,
  isDisabled,
  placeholder,
}) => {
  const t = useSafetyTranslation()
  const [allContacts, setAllContacts] = useState<IFormJBPHPContactChangeRequest[]>([])
  const [areContactsLoaded, setAreContactsLoaded] = useState(false)
  const autoSelectedKeyRef = useRef('')
  const {
    value: practiceBuilderValue,
    handleBlur,
    handleChange: handlePracticeBuilderChange,
  } = useFormField<string>({
    defaultValue: initialValue,
    formKey,
    field: 'practiceBuilder',
    isDisabled,
  })

  const practiceBuilderRole = roles[0] || ''
  const selectedPracticeBuilder =
    practiceBuilderValue !== undefined && practiceBuilderValue !== null
      ? String(practiceBuilderValue)
      : ''
  const translatedPlaceholder = placeholder ? t(placeholder) : undefined

  const jbpHpContactChangeRequestCrud = useMemo(
    () => getJbpHpContactsCrud(PRACTICE_BUILDER_CRUD_KEY),
    []
  )

  useEffect(() => {
    if (!partnerId || !practiceBuilderRole) {
      setAllContacts([])
      setAreContactsLoaded(false)

      return
    }

    let isMounted = true

    const fetchContacts = async () => {
      try {
        setAreContactsLoaded(false)
        jbpHpContactChangeRequestCrud.removeQueries(true)
        const filter = {
          logic: Logic.AND,
          filters: [
            {
              field: 'PartnerId',
              operator: Operator.EQUAL,
              value: partnerId,
            },
            {
              field: 'Roles',
              operator: Operator.CONTAINS,
              value: practiceBuilderRole,
            },
          ],
        }

        jbpHpContactChangeRequestCrud.setFilter(filter)
        jbpHpContactChangeRequestCrud.setPaging({
          page: 0,
          pageSize: PRACTICE_BUILDER_PAGE_SIZE,
        })
        const result = await jbpHpContactChangeRequestCrud.fetch()
        const contacts = result?.items || []

        if (!isMounted) {
          return
        }

        setAllContacts(contacts)
        setAreContactsLoaded(true)
      } catch {
        if (!isMounted) {
          return
        }

        setAllContacts([])
        setAreContactsLoaded(false)
      }
    }

    fetchContacts()

    return () => {
      isMounted = false
    }
  }, [jbpHpContactChangeRequestCrud, partnerId, practiceBuilderRole])

  const filteredContacts = useMemo<TPracticeBuilderOption[]>(() => {
    if (!strategicInitiative) {
      return []
    }

    return allContacts
      .filter((contact) => isBusinessUnitMatchingSI(contact.BusinessUnit, strategicInitiative))
      .filter((contact) => Boolean(contact.Email))
      .map((contact) => ({
        ...contact,
        value: contact.Email || '',
        fullName: getPracticeBuilderFullName(contact),
        displayLabel: getPracticeBuilderDisplayLabel(contact),
        legacyValue: String(contact.EntryId),
      }))
  }, [allContacts, strategicInitiative])

  useEffect(() => {
    if (!selectedPracticeBuilder || filteredContacts.length !== 1) {
      return
    }

    const [singleContact] = filteredContacts

    if (
      singleContact.value === selectedPracticeBuilder ||
      singleContact.legacyValue === selectedPracticeBuilder
    ) {
      autoSelectedKeyRef.current = `${partnerId}:${strategicInitiative || ''}:${
        singleContact.value
      }`
    }
  }, [
    filteredContacts,
    partnerId,
    practiceBuilderRole,
    selectedPracticeBuilder,
    strategicInitiative,
  ])

  useEffect(() => {
    if (!areContactsLoaded || selectedPracticeBuilder || filteredContacts.length !== 1) {
      return
    }

    const [singleContact] = filteredContacts
    const autoSelectedKey = `${partnerId}:${strategicInitiative || ''}:${singleContact.value}`

    if (autoSelectedKeyRef.current === autoSelectedKey) {
      return
    }

    autoSelectedKeyRef.current = autoSelectedKey
    void handlePracticeBuilderChange(singleContact.value)
  }, [
    areContactsLoaded,
    filteredContacts,
    handlePracticeBuilderChange,
    partnerId,
    practiceBuilderRole,
    selectedPracticeBuilder,
    strategicInitiative,
  ])

  useEffect(() => {
    if (!areContactsLoaded || !selectedPracticeBuilder || !strategicInitiative) {
      return
    }

    const isSelectedContactAvailable = filteredContacts.some(
      (contact) =>
        contact.value === selectedPracticeBuilder || contact.legacyValue === selectedPracticeBuilder
    )

    if (isSelectedContactAvailable) {
      return
    }

    void handlePracticeBuilderChange('')
  }, [
    areContactsLoaded,
    filteredContacts,
    handlePracticeBuilderChange,
    selectedPracticeBuilder,
    strategicInitiative,
  ])

  useEffect(() => {
    if (!areContactsLoaded || !selectedPracticeBuilder) {
      return
    }

    const selectedContact = filteredContacts.find(
      (contact) => contact.legacyValue === selectedPracticeBuilder
    )

    if (!selectedContact || selectedContact.value === selectedPracticeBuilder) {
      return
    }

    void handlePracticeBuilderChange(selectedContact.value)
  }, [areContactsLoaded, filteredContacts, handlePracticeBuilderChange, selectedPracticeBuilder])

  const crud = useMemo(() => {
    return new Crud({
      fetcher: new Static(filteredContacts),
      keyField: 'value',
      defaultPaging: {
        page: 0,
        pageSize: Math.max(filteredContacts.length, 1),
      },
    })
  }, [filteredContacts])

  const dropdownKey = useMemo(() => {
    return [
      partnerId,
      strategicInitiative || '',
      areContactsLoaded ? 'loaded' : 'loading',
      filteredContacts.map((contact) => contact.value).join('|'),
    ].join(':')
  }, [areContactsLoaded, filteredContacts, partnerId, strategicInitiative])

  return (
    <Dropdown
      key={dropdownKey}
      disabled={isDisabled}
      value={selectedPracticeBuilder}
      classes={{ controlClasses: { root: className } }}
      source={crud}
      caption="displayLabel"
      placeholder={translatedPlaceholder}
      onChange={handlePracticeBuilderChange}
      onBlur={handleBlur}
    />
  )
}

PracticeBuilder.displayName = 'PracticeBuilder'
